import type { CSSProperties } from "react";
import type { IssueRow } from "../data/monitoring";
import { InfoTip } from "./InfoTip";

type Movement = "rising" | "stable" | "declining" | "new";

function movementFor(row: IssueRow): Movement {
  const change = row.priority_index_change;
  if (change === null || change === undefined) return "new";
  if (Math.abs(change) < .1) return "stable";
  return change > 0 ? "rising" : "declining";
}

function priorityColor(position: number, length: number) {
  const proportion = length <= 1 ? 0 : position / (length - 1);
  const from = [255, 30, 0];
  const to = [174, 184, 201];
  return `rgb(${from.map((channel, index) => Math.round(channel + (to[index] - channel) * proportion)).join(", ")})`;
}

export function PriorityIndexPlot({ rows, currentOnly = false, selectedTheme = null, onSelect }: { rows: IssueRow[]; currentOnly?: boolean; selectedTheme?: string | null; onSelect?: (theme: string) => void }) {
  const rankedRows = rows
    .filter((row) => row.priority_score !== null)
    .sort((left, right) => (right.priority_score ?? 0) - (left.priority_score ?? 0));
  const maximum = Math.max(...rankedRows.map((row) => row.priority_score ?? 0), 1);
  const ceiling = Math.ceil(maximum * 1.15 * 2) / 2;

  return (
    <div className={`priority-index-plot ${currentOnly ? "priority-index-plot--current-only" : ""}`}>
      <div className="priority-index-plot__header">
        <div>
          <span className="visual-kicker">Priority Ranking</span>
          <h3>Highest-Priority Issues</h3>
        </div>
        <InfoTip label="How to read Priority Index">
          Priority Index combines conversation share with negative sentiment, observed toxicity, and lack of positivity. Higher scores indicate a larger, more risk-weighted conversation footprint.{!currentOnly && " Priority Change compares the score with the prior period."}
        </InfoTip>
      </div>
      <div className="priority-index-plot__columns" aria-hidden="true">
        <span className="priority-index-plot__column-heading">Priority<br /><span className="priority-index-plot__column-heading-word">Score</span></span>
        {!currentOnly && <span className="priority-index-plot__column-heading">Priority<br /><span className="priority-index-plot__column-heading-word">Change</span></span>}
      </div>
      <div className="priority-index-plot__rows" role="group" aria-label="Priority Index Ranking">
        {rankedRows.map((row, index) => {
          const score = row.priority_score ?? 0;
          const width = `${Math.max(4, (score / ceiling) * 100)}%`;
          const movement = movementFor(row);
          const movementLabel = movement === "rising" ? "▲ Rising" : movement === "declining" ? "▼ Decline" : movement === "new" ? "▶ New" : "▶ Stable";
          const change = row.priority_index_change;
          const changeLabel = change === null || change === undefined ? "—" : `${change >= 0 ? "+" : "−"}${Math.abs(change).toFixed(2)}`;
          const color = priorityColor(index, rankedRows.length);
          return (
            <div className="priority-index-plot__row" key={row.theme}>
              <span className="priority-index-plot__rank">{String(index + 1).padStart(2, "0")}</span>
              <div className="priority-index-plot__theme"><b>{row.theme}</b>{!currentOnly && <small className={`priority-index-plot__movement priority-index-plot__movement--${movement}`}>{movementLabel}</small>}</div>
              <div className="priority-index-plot__track">
                <button type="button" className={`priority-index-plot__bar ${selectedTheme && selectedTheme !== row.theme ? "is-muted" : ""}`} style={{ width, background: color }} aria-label={`Select ${row.theme}`} onClick={() => onSelect?.(row.theme)} />
              </div>
              <span className="priority-index-plot__score" style={{ "--priority-color": color } as CSSProperties}>{score.toFixed(2)}</span>
              {!currentOnly && <span className={`priority-index-plot__change priority-index-plot__change--${movement}`}>{changeLabel}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
